import React, { useState, useEffect } from 'react';
import { supabase } from '../supabase';
import { toastError, toastSuccess } from '../ui/toast';
import { loadSessionsForClients, loadTrainedOpenSessionIds, summariseClientSessions } from '../services/sessionAccountingService';
import { colors as theme } from '../theme';

const colors = {
  bg: theme.bg,
  surface1: theme.surface1,
  surface2: theme.surface2,
  surface3: theme.surface3,
  border: 'rgba(42, 53, 98, 0.8)',
  text: theme.text,
  textSecondary: '#C0C8DC',
  textMuted: theme.textMuted,
  accent: theme.gold,
  success: theme.success,
  error: theme.danger,
};

const timeAgo = (dateStr) => {
  if (!dateStr) return 'Never';
  const now = new Date();
  const session = new Date(dateStr);
  // Compare by calendar date, not raw hours
  const toMidnight = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const days = Math.round((toMidnight(now) - toMidnight(session)) / (1000 * 60 * 60 * 24));
  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days}d ago`;
  if (days < 30) return `${Math.floor(days / 7)}w ago`;
  return `${Math.floor(days / 30)}mo ago`;
};

const activityColor = (days) => {
  if (days === null) return colors.textMuted;
  if (days <= 3) return colors.success;
  if (days <= 7) return colors.accent;
  return colors.error;
};

// Client Activity: who trained, and open flags. "Needs you" moved to Manage Clients (plan 004,
// hooks/useNeedsYou.js).
const CoachDashboard = ({ onViewClient }) => {
  const [clients, setClients] = useState([]);
  const [clientStats, setClientStats] = useState({});
  const [allFlags, setAllFlags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [resolvingFlag, setResolvingFlag] = useState(null);
  const [resolveNote, setResolveNote] = useState('');
  const [resolving, setResolving] = useState(false);

  useEffect(() => { loadDashboard(); }, []);

  const loadDashboard = async () => {
    setLoading(true);
    setLoadFailed(false);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const { data: me } = await supabase
        .from('users').select('id, auth_user_id').eq('auth_user_id', user.id).single();

      const { data: clientData } = await supabase
        .from('users')
        .select('id, auth_user_id, name, status, subscription_status, is_complimentary, created_at')
        .eq('coach_id', me.auth_user_id)
        .eq('role', 'client')
        .order('name');

      if (!clientData?.length) { setLoading(false); return; }
      setClients(clientData);

      const authIds = clientData.map(c => c.auth_user_id);

      const [sessions, assignmentsRes, flagsRes] = await Promise.all([
        loadSessionsForClients(authIds),
        supabase
          .from('program_assignments')
          .select('user_id, current_week')
          .in('user_id', authIds)
          .eq('is_active', true),
        supabase
          .from('exercise_flags')
          .select('id, user_id, exercise_name, notes, flagged_at, program_exercise_id, exercise_id')
          .in('user_id', authIds)
          .is('resolved_at', null)
          .order('flagged_at', { ascending: false }),
      ]);

      if (assignmentsRes.error) throw assignmentsRes.error;
      if (flagsRes.error) throw flagsRes.error;
      // A session row exists as soon as a client opens a day, so the open ones need telling apart:
      // which had a set completed in them, and which were only opened.
      const trainedOpenIds = await loadTrainedOpenSessionIds(sessions);
      const assignments = assignmentsRes.data || [];
      const flags = flagsRes.data || [];
      const stats = {};
      const now = new Date();

      for (const client of clientData) {
        const uid = client.auth_user_id;
        const assignment = assignments.find(a => a.user_id === uid);
        stats[uid] = {
          ...summariseClientSessions(sessions.filter(s => s.user_id === uid), trainedOpenIds, now),
          currentWeek: assignment?.current_week || null,
          flagCount: flags.filter(f => f.user_id === uid).length,
        };
      }

      setClientStats(stats);
      setAllFlags(flags);
    } catch (err) {
      console.error('[CoachDashboard] load error:', err);
      // Without this the cards render with no numbers, which reads as "nobody has trained".
      setLoadFailed(true);
    }
    setLoading(false);
  };

  const handleQuickResolve = async (flagId) => {
    setResolving(true);
    const { data: { user } } = await supabase.auth.getUser();
    const { error } = await supabase
      .from('exercise_flags')
      .update({
        resolved_at: new Date().toISOString(),
        resolved_by: user.id,
        resolution_notes: resolveNote.trim() || null,
      })
      .eq('id', flagId);

    setResolving(false);
    if (error) { toastError('Could not resolve flag'); return; }

    const flag = allFlags.find(f => f.id === flagId);
    setAllFlags(prev => prev.filter(f => f.id !== flagId));
    if (flag) {
      setClientStats(prev => {
        const uid = flag.user_id;
        return { ...prev, [uid]: { ...prev[uid], flagCount: Math.max(0, (prev[uid]?.flagCount || 1) - 1) } };
      });
    }
    setResolvingFlag(null);
    setResolveNote('');
    toastSuccess('Flag resolved');
  };

  if (loading) {
    return <div style={{ padding: '40px', color: colors.textMuted, textAlign: 'center' }}>Loading...</div>;
  }

  if (!clients.length) {
    return <div style={{ padding: '40px', color: colors.textMuted, textAlign: 'center' }}>No clients assigned yet.</div>;
  }

  return (
    <div style={{ paddingBottom: '40px' }}>

      {loadFailed && (
        <div style={{ marginBottom: '14px', padding: '10px 14px', borderRadius: '8px', border: `1px solid ${colors.error}`, color: colors.error, fontSize: '13px' }}>
          Could not load client activity, so the numbers below are missing.{' '}
          <button onClick={loadDashboard} style={{ background: 'none', border: 'none', color: colors.error, textDecoration: 'underline', cursor: 'pointer', fontSize: '13px', padding: 0 }}>
            Try again
          </button>
        </div>
      )}

      {/* Client Activity */}
      <div style={{ marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '8px' }}>
        <span style={{ color: colors.textMuted, fontSize: '11px', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase' }}>
          Client Activity
        </span>
        <div style={{ flex: 1, height: '1px', backgroundColor: colors.border }} />
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '32px' }}>
        {clients.map(client => {
          const stats = clientStats[client.auth_user_id] || {};
          const color = activityColor(stats.daysSinceLast);
          return (
            <div
              key={client.id}
              onClick={() => onViewClient?.(client)}
              style={{
                backgroundColor: colors.surface1,
                border: `1px solid ${colors.border}`,
                borderRadius: '14px',
                padding: '14px 16px',
                cursor: onViewClient ? 'pointer' : 'default',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                transition: 'border-color 0.15s',
              }}
              onMouseEnter={e => { if (onViewClient) e.currentTarget.style.borderColor = colors.accent; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = colors.border; }}
            >
              {/* Activity indicator dot */}
              <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: color, flexShrink: 0 }} />

              {/* Name + last session */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ color: colors.text, fontSize: '15px', fontWeight: 600, marginBottom: '2px' }}>
                  {client.name}
                </div>
                <div style={{ color: colors.textMuted, fontSize: '12px', display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
                  <span>
                    {stats.lastEnergyRating === 3 ? '🔥 ' : stats.lastEnergyRating === 2 ? '😐 ' : stats.lastEnergyRating === 1 ? '😴 ' : ''}
                    Last: <span style={{ color }}>{timeAgo(stats.lastSessionDate)}</span>
                  </span>
                  {stats.currentWeek && <span>Week {stats.currentWeek}</span>}
                  {stats.totalSessions > 0 && (
                    <span>{stats.completedSessions}/{stats.totalSessions} sessions</span>
                  )}
                  {stats.skippedDays > 0 && (
                    <span style={{ color: colors.textMuted }}>
                      {stats.skippedDays} day{stats.skippedDays > 1 ? 's' : ''} skipped
                    </span>
                  )}
                </div>
              </div>

              {/* Completion % + flag badge */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 }}>
                {stats.completionRate !== null && (
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ color: colors.text, fontSize: '16px', fontWeight: 700, lineHeight: 1 }}>
                      {stats.completionRate}%
                    </div>
                    <div style={{ color: colors.textMuted, fontSize: '11px', letterSpacing: '0.5px', marginTop: '2px' }}>
                      DONE
                    </div>
                  </div>
                )}
                {stats.flagCount > 0 && (
                  <div style={{
                    backgroundColor: 'rgba(255,59,48,0.12)',
                    border: '1px solid rgba(255,59,48,0.3)',
                    borderRadius: '20px',
                    padding: '4px 9px',
                    fontSize: '12px',
                    fontWeight: 700,
                    color: colors.error,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                  }}>
                    🚩 {stats.flagCount}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Flags Triage */}
      <div style={{ marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '8px' }}>
        <span style={{ color: colors.textMuted, fontSize: '11px', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase' }}>
          Unresolved Flags
        </span>
        {allFlags.length > 0 && (
          <span style={{
            backgroundColor: 'rgba(255,59,48,0.15)',
            color: colors.error,
            borderRadius: '8px',
            padding: '2px 8px',
            fontSize: '11px',
            fontWeight: 700,
          }}>
            {allFlags.length}
          </span>
        )}
        <div style={{ flex: 1, height: '1px', backgroundColor: colors.border }} />
      </div>

      {allFlags.length === 0 ? (
        <div style={{
          color: colors.success,
          fontSize: '13px',
          textAlign: 'center',
          padding: '16px',
          backgroundColor: 'rgba(52,199,89,0.06)',
          borderRadius: '8px',
          border: '1px solid rgba(52,199,89,0.15)',
        }}>
          ✓ No unresolved flags — all clear
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {allFlags.map(flag => {
            const client = clients.find(c => c.auth_user_id === flag.user_id);
            const isResolving = resolvingFlag === flag.id;
            return (
              <div
                key={flag.id}
                style={{
                  backgroundColor: colors.surface1,
                  border: `1px solid ${isResolving ? colors.accent : colors.border}`,
                  borderRadius: '14px',
                  padding: '12px 14px',
                  transition: 'border-color 0.15s',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '10px' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '5px', flexWrap: 'wrap' }}>
                      <span style={{
                        color: colors.accent,
                        fontSize: '11px',
                        fontWeight: 700,
                        backgroundColor: 'rgba(212,175,55,0.1)',
                        padding: '2px 8px',
                        borderRadius: '8px',
                      }}>
                        {client?.name || 'Unknown'}
                      </span>
                      <span style={{ color: colors.textMuted, fontSize: '11px' }}>
                        {new Date(flag.flagged_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                      </span>
                    </div>
                    <div style={{ color: colors.text, fontSize: '14px', fontWeight: 600, marginBottom: '3px' }}>
                      {flag.exercise_name || 'Unknown exercise'}
                    </div>
                    {flag.notes && (
                      <div style={{ color: colors.textMuted, fontSize: '12px', fontStyle: 'italic' }}>
                        "{flag.notes}"
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => {
                      if (isResolving) { setResolvingFlag(null); setResolveNote(''); }
                      else { setResolvingFlag(flag.id); setResolveNote(''); }
                    }}
                    style={{
                      backgroundColor: isResolving ? 'transparent' : 'rgba(212,175,55,0.1)',
                      border: `1px solid ${isResolving ? colors.border : colors.accent}`,
                      borderRadius: '8px',
                      color: isResolving ? colors.textMuted : colors.accent,
                      fontSize: '12px',
                      fontWeight: 600,
                      padding: '6px 12px',
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                      flexShrink: 0,
                    }}
                  >
                    {isResolving ? 'Cancel' : 'Resolve'}
                  </button>
                </div>

                {isResolving && (
                  <div style={{ marginTop: '12px', borderTop: `1px solid ${colors.border}`, paddingTop: '12px' }}>
                    <input
                      type="text"
                      placeholder="Note to client (optional) — e.g. try cable fly instead"
                      value={resolveNote}
                      onChange={e => setResolveNote(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') handleQuickResolve(flag.id); }}
                      autoFocus
                      style={{
                        width: '100%',
                        backgroundColor: colors.surface2,
                        border: `1px solid ${colors.border}`,
                        borderRadius: '8px',
                        color: colors.text,
                        fontSize: '13px',
                        padding: '9px 12px',
                        marginBottom: '10px',
                        boxSizing: 'border-box',
                        outline: 'none',
                      }}
                    />
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        onClick={() => handleQuickResolve(flag.id)}
                        disabled={resolving}
                        style={{
                          flex: 1,
                          backgroundColor: colors.success,
                          border: 'none',
                          borderRadius: '8px',
                          color: colors.bg,
                          fontSize: '13px',
                          fontWeight: 700,
                          padding: '10px',
                          cursor: resolving ? 'not-allowed' : 'pointer',
                          opacity: resolving ? 0.6 : 1,
                        }}
                      >
                        {resolving ? 'Saving...' : '✓ Mark Resolved'}
                      </button>
                      {onViewClient && client && (
                        <button
                          onClick={() => onViewClient(client)}
                          style={{
                            backgroundColor: 'rgba(212,175,55,0.08)',
                            border: `1px solid ${colors.accent}`,
                            borderRadius: '8px',
                            color: colors.accent,
                            fontSize: '13px',
                            fontWeight: 600,
                            padding: '10px 14px',
                            cursor: 'pointer',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          Full View →
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CoachDashboard;
